import { EventEmitterX } from '../../../helpers/events';
import { uuid } from '../../../helpers/common';

export default class DrawingTools extends EventEmitterX {
    constructor(map, google) {
        super();

        this.map = map;
        this.google = google;

        this.shapeObj = null;
        this.shapes = {};
        this.shapeGroups = {};
        this.isEditable = false;
        this.drawingManager = null;
        this.selectedShape = null;
        this.selectedOptions = {
            strokeColor: null,
            fillColor: null
        };

        this.polyOptions = {
            strokeWeight: 2,
            strokeOpacity: 1,
            fillOpacity: 1,
            editable: true,
            draggable: true
        };

        this.newShapeCount = 0;
        this.newShapes = [];
        this.isRegEdit = false;
        this.plugins = {};
    }

    openTools() {
        if (!this.drawingManager) this.createTools();
        if (this.drawingManager) {
            this.drawingManager.setMap(this.map);
            this.clearSelection();
            this.setEditable(true);
        }
        return this;
    }

    closeTools() {
        if (this.drawingManager) {
            this.drawingManager.setMap(null);
            this.clearSelection();
            this.setEditable(false);
        }
        return this;
    }

    drawShapeGroups(shapeGroups) {
        const newShapeGroups = {};
        let newGeoShapes = {};

        for (const shapeGroupId in shapeGroups) {
            const item = shapeGroups[shapeGroupId];
            newShapeGroups[shapeGroupId] = {
                name: item.name, cropName: item.cropName, typeId: item.type,
                visible: item.visible, isPreLoaded: item.isPreLoaded,
                customerName: item.customerName, variety: item.variety
            }

            if (!item.shapes) continue;
            newGeoShapes = { ...newGeoShapes, ...item.shapes };
        }

        this.shapeGroups = { ...this.shapeGroups, ...newShapeGroups };
        this.drawShapes(newGeoShapes);
    }

    drawShapes(geoShapes) {
        const newShapes = {};
        const shapeObj = this.getShapeInstance();

        for (const shapeId in geoShapes) {
            const item = geoShapes[shapeId];
            const properties = { ...item.properties, zIndex: 1 };
            const shape = shapeObj.createShape({ ...item, properties, id: shapeId });
            if (!shape) continue;

            shape.type = item.type;
            this._addShapeEventListener(shape, false);

            newShapes[shapeId] = {
                shape, shapeGroupId: item.shapeGroupId, shapeRefId: item.shapeRefId, name: item.name,   // shape name
                isPreLoaded: true, status: 0, geoJSON: item, isAutoName: false, tareFactor: item.tareFactor
            };
        }

        this.shapes = { ...this.shapes, ...newShapes };
        this.showShapes();
        return this;
    }

    editableShapeGroup(shapeGroupId) {
        this.clearSelection();
        this.editableShapeGroupId = shapeGroupId;

        if (!this.shapeGroups[shapeGroupId]) {
            this.isRegEdit = false;
            this.newShapes = [];
            const geoShapeGroup = {};

            geoShapeGroup[shapeGroupId] = {
                name: "Untitled",
                type: 2,
                cropName: "",
                visible: true,
                isPreLoaded: false,
                variety: '',
            };
            this.drawShapeGroups(geoShapeGroup);
        }
        else {
            const editableShapes = [];
            this.isRegEdit = true;
            for (const shapeId in this.shapes) {
                const item = this.shapes[shapeId];
                if (!item.shape) return;

                if (shapeGroupId && item.shapeGroupId === shapeGroupId) {
                    editableShapes.push(item);
                    this.visibleShape(item.shape, true);
                    item.shape.setOptions({ draggable: true });
                }
                else
                    item.shape.setOptions({ editable: false, draggable: false });
            }

            if (editableShapes.length !== 0) {
                this.newShapeCount = editableShapes.length;
                this.newShapes = [];
            }

            if (this.shapeGroups[shapeGroupId] && !this.shapeGroups[shapeGroupId].visible) {
                this.shapeGroups[shapeGroupId].visible = true;
            }
        }
        return this;
    }

    editShapeGroupDetail(data, shapeGroupId) {
        if (typeof shapeGroupId === 'undefined') shapeGroupId = this.editableShapeGroupId;

        this.shapeGroups[shapeGroupId] = {
            ...this.shapeGroups[shapeGroupId],
            name: data.name,
            cropName: data.cropName,
            type: data.type,
            customerName: data.customerName
        };

        for (const id in this.shapes) {
            if ((this.shapes[id].shapeGroupId === shapeGroupId) && this.shapes[id].isAutoName) {
                const shapeName = this.shapes[id].name.split("#")[1] ? `${data.name} #${this.shapes[id].name.split("#")[1]}` : data.name;
                this.shapes[id].name = shapeName;
            }
        }
    }

    createTools() {
        // Creates a drawing manager attached to the map that allows the user to draw
        // markers, lines, and shapes.
        this.drawingManager = new this.google.maps.drawing.DrawingManager({
            drawingMode: this.google.maps.drawing.OverlayType.POLYGON,
            drawingControlOptions: {
                position: this.google.maps.ControlPosition.TOP_CENTER,
                drawingModes: [
                    this.google.maps.drawing.OverlayType.MARKER,
                    this.google.maps.drawing.OverlayType.POLYGON,
                    this.google.maps.drawing.OverlayType.POLYLINE,
                    // google.maps.drawing.OverlayType.CIRCLE,
                    //google.maps.drawing.OverlayType.RECTANGLE,
                ],
            },
            markerOptions: {
                draggable: true
            },
            polylineOptions: {
                editable: true,
                draggable: true
            },
            rectangleOptions: this.polyOptions,
            circleOptions: this.polyOptions,
            polygonOptions: this.polyOptions,
            map: null
        });

        return this._addManagerEventListener();
    }

    _addManagerEventListener() {
        const google = this.google;
        const me = this;

        google.maps.event.addListener(this.drawingManager, 'overlaycomplete', e => {
            const newShape = e.overlay;
            newShape.type = e.type;
            this.addShapeToList(newShape);
            this._addShapeEventListener(newShape);
        });

        // Clear the current selection when the drawing mode is changed, or when the
        // map is clicked.
        google.maps.event.addListener(this.drawingManager, 'drawingmode_changed', () => me.clearSelection());
        google.maps.event.addListener(this.map, 'click', () => me.clearSelection());
        if (this.selectedOptions.fillColor) this.setDrawingOptions(this.selectedOptions);
        return this;
    }

    _addShapeEventListener(newShape, isNewShape = true) {
        const google = this.google;
        const me = this;

        if (newShape.type === google.maps.drawing.OverlayType.MARKER) {
            google.maps.event.addListener(newShape, 'click', e => {
                me.openShapeInfowindow(newShape);
                if (me.selectedShape !== newShape) me.setSelection(newShape);
            });

            google.maps.event.addListener(newShape, 'mouseup', () => {
                if (me.selectedShape !== newShape) me.setSelection(newShape);
            });


            google.maps.event.addListener(newShape, 'dblclick', () => {
                me.closeShapeInfowindow();
                if (me.selectedShape !== newShape) me.shapeDoubleClickEvent(newShape);
            });
        }
        else {
            // Add an event listener that selects the newly-drawn shape when the user
            // mouses down on it.
            google.maps.event.addListener(newShape, 'click', e => {
                if (!e) return;
                if (e.vertex !== undefined) {
                    if (newShape.type === google.maps.drawing.OverlayType.POLYGON) {
                        const path = newShape.getPaths().getAt(e.path);
                        path.removeAt(e.vertex);
                        if (path.length < 3) {
                            newShape.setMap(null);
                        }
                    }
                    if (newShape.type === google.maps.drawing.OverlayType.POLYLINE) {
                        const path = newShape.getPath();
                        path.removeAt(e.vertex);
                        if (path.length < 2) {
                            newShape.setMap(null);
                        }
                    }
                }

                me.openShapeInfowindow(newShape);
                if (me.selectedShape !== newShape) me.setSelection(newShape);
            });

            google.maps.event.addListener(newShape, 'mouseup', () => {
                if (me.selectedShape !== newShape) me.setSelection(newShape);
            });

            google.maps.event.addListener(newShape, 'dblclick', () => {
                me.closeShapeInfowindow();
                if (me.selectedShape !== newShape) me.shapeDoubleClickEvent(newShape);
            });
        }

        google.maps.event.addListener(newShape, 'dragend', () => this.editShapeFromList(newShape));
        const type = google.maps.drawing.OverlayType
        if ([type.CIRCLE, type.RECTANGLE].indexOf(newShape.type) >= 0) {
            google.maps.event.addListener(newShape, 'bounds_changed', () => this.editShapeFromList(newShape));
        }
        else if ([type.POLYGON, type.POLYLINE].indexOf(newShape.type) >= 0) {
            google.maps.event.addListener(newShape.getPath(), 'insert_at', () => this.editShapeFromList(newShape));
            google.maps.event.addListener(newShape.getPath(), 'remove_at', () => this.editShapeFromList(newShape));
            google.maps.event.addListener(newShape.getPath(), 'set_at', () => this.editShapeFromList(newShape));
        }

        if (isNewShape) {
            me.setNonDrawingMode();
            me.setSelection(newShape);
        }

        return this;
    }

    openShapeInfowindow(shape) {
        if (!this.shapes[shape.id]) return this;
        this.closeShapeInfowindow();

        //infowindow
        const me = this;
        const shapeId = shape.id;
        const shapeItem = this.shapes[shapeId];
        const data = this.getShapeDetail(shapeId);
        const popupElement = document.createElement("div");
        popupElement.setAttribute("id", `infowindow_shape_${shapeId}`);
        if (data.geoJSON === undefined) return;
        const location = data.geoJSON.location;
        const latitude = location.map(e => e.lat);
        const maximumLatitude = Math.max.apply(Math, latitude);
        const longitudeAverage = location.reduce((a, b) => {
            return a + b.lng;
        }, 0);
        this.emit("shape_infowindow_open", { popupElement, data });
        const position = { lat: maximumLatitude, lng: longitudeAverage / location.length };
        const infowindow = new this.google.maps.InfoWindow({ content: popupElement, position, zIndex: 1000 });
        infowindow.addListener('closeclick', function () { me.emit("shape_infowindow_close", { popupElement, data }); });
        infowindow.open(this.map);

        if (infowindow && shape.type === "marker") {
            this.google.maps.event.addListener(infowindow, 'domready', function () {
                const element = document.getElementById(`infowindow_shape_${shapeId}`);
                if (!element) return;
                const parent = element.parentElement.parentElement.parentElement;
                if (!parent) return;
                parent.style.bottom = "50px";
            });
        }

        shapeItem.infowindow = infowindow;
        return this;
    }

    closeShapeInfowindow(shape) {
        if (!shape) {
            for (const shapeId in this.shapes) {
                const shapeItem = this.shapes[shapeId];
                if (shapeItem.infowindow) {
                    this.emit("shape_infowindow_close", { popupElement: shapeItem.infowindow.getContent() });
                    shapeItem.infowindow.close();
                }
            }
            return this;
        }

        if (!this.shapes[shape.id]) return this;
        const shapeItem = this.shapes[shape.id];
        if (shapeItem.infowindow) {
            this.emit("shape_infowindow_close", { popupElement: shapeItem.infowindow.getContent() });
            shapeItem.infowindow.close();
        }

        return this;
    }

    shapeDoubleClickEvent(shape) {
        const item = this.shapes[shape.id] ? this.shapes[shape.id] : null;
        if (!item) return;
        this.emit("shape_dblclick", { shape, shapeGroupId: item.shapeGroupId });
        this.setSelection(shape);
    }

    setNonDrawingMode() {
        if (this.drawingManager) this.drawingManager.setDrawingMode(null);
        return this;
    }

    clearSelection() {
        if (this.selectedShape) {
            this.setShapeEditable(this.selectedShape, false);
            this.selectedShape = null;
        }
        return this;
    }

    setSelection(shape) {
        if (!shape) return this;
        if (!this.getEditable()) return this;
        if (!this.isEditableShape(shape)) return this;

        let options = null;
        this.clearSelection();
        this.setShapeEditable(shape, true);

        if (shape.type !== this.google.maps.drawing.OverlayType.MARKER) {
            options = {
                fillColor: shape.get('fillColor'),
                strokeColor: shape.get('strokeColor')
            };
            this.setDrawingOptions(options);
        }

        this.selectedShape = shape;
        this.emit("shape_selected", { shape, options });
        return this;
    }

    setShapeEditable(shape, editable) {
        shape.setOptions({ editable, draggable: editable });
        return this;
    }

    isEditableShape(shape) {
        const shapeItem = this.shapes[shape.id] ? this.shapes[shape.id] : null;
        if (!shapeItem) return false;
        return (shapeItem.shapeGroupId === this.editableShapeGroupId);
    }

    deleteSelectedShape() {
        if (this.selectedShape) {
            this.selectedShape.setMap(null);
            this.closeShapeInfowindow(this.selectedShape);
            this.removeShapeFromList(this.selectedShape);
        }
        return this;
    }

    setColor(color) {
        const options = { ...this.selectedOptions, ...color };
        this.setDrawingOptions(options);
        this.setSelectedShapeColor(options);
        if (this.selectedShape) this.editShapeFromList(this.selectedShape);
        return this;
    }

    setDrawingOptions(options) {
        this.selectedOptions = options;
        if (!this.drawingManager) return this;

        const polylineOptions = this.drawingManager.get('polylineOptions');
        polylineOptions.strokeColor = options.strokeColor;
        this.drawingManager.set('polylineOptions', polylineOptions);

        const rectangleOptions = this.drawingManager.get('rectangleOptions');
        rectangleOptions.fillColor = options.fillColor;
        rectangleOptions.strokeColor = options.strokeColor;
        this.drawingManager.set('rectangleOptions', rectangleOptions);

        const circleOptions = this.drawingManager.get('circleOptions');
        circleOptions.fillColor = options.fillColor;
        circleOptions.strokeColor = options.strokeColor;
        this.drawingManager.set('circleOptions', circleOptions);

        const polygonOptions = this.drawingManager.get('polygonOptions');
        polygonOptions.fillColor = options.fillColor;
        circleOptions.strokeColor = options.strokeColor;
        this.drawingManager.set('polygonOptions', polygonOptions);
    }

    setSelectedShapeColor(color) {
        if (this.selectedShape) {
            if (this.selectedShape.type === this.google.maps.drawing.OverlayType.POLYLINE)
                this.selectedShape.set('strokeColor', color.strokeColor);
            else {
                this.selectedShape.set('fillColor', color.fillColor);
                this.selectedShape.set('strokeColor', color.strokeColor);
            }
        }

        return this;
    }

    addShapeToList(shape) {
        const shapeId = shape && shape.id ? shape.id : uuid();
        shape.id = shapeId;

        const shapeName = shape && shape.name ? shape.name : this.getUntitledShapeName(this.shapeGroups[this.editableShapeGroupId].name);
        this.shapes[shapeId] = {
            shape, shapeGroupId: this.editableShapeGroupId,
            isPreLoaded: false, status: 1, name: shapeName,
            isAutoName: shape && shape.name ? false : true,
            folder_name: shape.folder_name ? shape.folder_name : 'Untitled',
        };

        this.newShapeCount = this.newShapeCount + 1;
        this.newShapes = [...this.newShapes, this.shapes[shapeId]];
        return this;
    }

    removeShapeFromList(shape) {
        const shapeId = (typeof shape === "string") ? shape : shape.id;
        if (this.shapes[shapeId]) {
            if (!this.shapes[shapeId].isPreLoaded) {
                delete this.shapes[shapeId]
                this.newShapeCount = this.newShapeCount === 0 ? 0 : this.newShapeCount - 1;
            }
            else this.shapes[shapeId].status = 3;
        }
        return this;
    }

    editShapeFromList(shape) {
        const shapeId = (typeof shape === "string") ? shape : shape.id;
        if (this.shapes[shapeId] && this.shapes[shapeId].isPreLoaded)
            this.shapes[shapeId].status = 2;
        return this;
    }

    editShapeDetail(shape, { name, tare_factor }) {
        const shapeId = (typeof shape === "string") ? shape : shape.id;
        if (this.shapes[shapeId]) {
            this.shapes[shapeId].name = name;
            this.shapes[shapeId].tare_factor = tare_factor;
            this.shapes[shapeId].isAutoName = false;
        }
        this.editShapeFromList(shapeId);
    }

    getUntitledShapeName(shapeName) {
        const foundEditableShapes = Object.keys(this.shapes).filter(id =>
            (this.shapes[id].shapeGroupId === this.editableShapeGroupId && !this.shapes[id].isPreLoaded));
        return `${foundEditableShapes.length === 0 ? shapeName : `${shapeName} #${foundEditableShapes.length + 1}`}`;
    }

    getShapeGeo(shapeGroupId) {
        const shapes = {};
        for (const shapeId in this.shapes) {
            const item = this.shapes[shapeId];
            if (item.shapeGroupId !== shapeGroupId) continue;

            const { properties, location } = this.getShapeInstance().getShapeOptions(item.shape, true);
            shapes[shapeId] = {
                type: item.shape.type,
                status: item.status,
                name: item.name,
                folder_name: item.folder_name ? item.folder_name : 'Untitled',
                properties, location,
                tare_factor: item.tare_factor
            };
        }

        return shapes;
    }

    showShapeGroups(shapeGroupIds, isForce) {
        const isShapeGroupEmpty = typeof shapeGroupIds === 'undefined' || shapeGroupIds === null;

        for (const shapeGroupId in this.shapeGroups) {
            if (isForce) {
                this.shapeGroups[shapeGroupId].visible =
                    isShapeGroupEmpty
                        ? this.shapeGroups[shapeGroupId].visible
                        : (shapeGroupIds || []).indexOf(shapeGroupId) >= 0 || this.editableShapeGroupId === shapeGroupId;
                continue;
            }
            if (isShapeGroupEmpty
                || shapeGroupIds.indexOf(shapeGroupId) >= 0
                || this.editableShapeGroupId === shapeGroupId)
                this.shapeGroups[shapeGroupId].visible = true;
            else this.shapeGroups[shapeGroupId].visible = false;
        }

        this.showShapes();
        return this;
    }

    showShapes() {
        for (const shapeId in this.shapes) {
            const item = this.shapes[shapeId];
            if (!item.shape) continue;

            this.shapeGroups[item.shapeGroupId] && this.shapeGroups[item.shapeGroupId].visible ?
                this.visibleShape(item.shape, true) : this.visibleShape(item.shape, false);
        }

        return this;
    }

    visibleShape(shape, visible) {
        if (typeof shape === "string" && this.shapes[shape.id] && this.shapes[shape.id].shape)
            shape = this.shapes[shape.id].shape;
        if (!shape) return this;

        if (visible) {
            if (!shape.getMap()) shape.setMap(this.map);
            this.emit("shape_visible", { shapeId: shape.id, visible });
        } else {
            this.closeShapeInfowindow(shape)
            if (shape.getMap()) shape.setMap(null);
            this.emit("shape_visible", { shapeId: shape.id, visible });
        }
    }

    setBounds(shapeGroupIds) {
        if (typeof shapeGroupIds === "string") shapeGroupIds = [shapeGroupIds];

        let bounds = null;
        for (const shapeId in this.shapes) {
            const item = this.shapes[shapeId];
            if (shapeGroupIds && shapeGroupIds.indexOf(item.shapeGroupId) < 0) continue;
            if (!item.shape) continue;

            const location = this.getShapeInstance().getLocation(item.shape, true);
            for (const coordinate of location) {
                if (coordinate.lat() === -90) continue;
                if (!bounds) bounds = new this.google.maps.LatLngBounds();
                bounds.extend(coordinate);
            }
        }

        if (bounds) this.map.fitBounds(bounds);
        return this;
    }

    setBoundsForShape(shapeId) {
        let bounds = null;
        let shape = null;
        Object.keys(this.shapes).forEach((id) => {
            let item = this.shapes[id];
            if (item.geoJSON) {
                if (item.geoJSON.shapeRefId === shapeId) {
                    const location = this.getShapeInstance().getLocation(item.shape, true);
                    for (const coordinate of location) {
                        if (coordinate.lat() === -90) continue;
                        if (!bounds) bounds = new this.google.maps.LatLngBounds();
                        bounds.extend(coordinate);
                    }
                    shape = item.shape;
                }
            }
        })

        if (bounds) this.map.fitBounds(bounds);
        if (shape) this.setShapeEditable(shape, true);
        return this;
    }

    clearShapes() {
        this.closeShapeInfowindow();
        for (const shapeId in this.shapes) {
            const item = this.shapes[shapeId];
            if (item.shape) {
                item.shape.setMap(null);
                delete this.shapes[shapeId];
            }
        }
        return this;
    }

    reset() {
        this.editableShapeGroupId = null;
        this.selectedShape = null;
        this.newShapeCount = 0;
        this.isRegEdit = false;
        return this;
    }

    discard() {
        const shapesGeo = {};
        for (const shapeId in this.shapes) {
            if (!this.shapes[shapeId].geoJSON) continue;
            shapesGeo[shapeId] = this.shapes[shapeId].geoJSON;
        };

        if (!this.isRegEdit) {
            this.newShapeCount = 0;
            this.newShapes = [];
        }
        return this.clearShapes().drawShapes(shapesGeo);
    }

    dispose() {
        this.clearShapes();
        if (this.drawingManager) {
            this.drawingManager.setMap(null);
            this.drawingManager = null;
        }
        return this;
    }

    setEditable(editable) {
        this.isEditable = editable;
        if (!editable) {
            for (const shapeId in this.shapes) {
                if (this.shapes[shapeId].shape)
                    this.shapes[shapeId].shape.setOptions({ editable: false, draggable: false });
            }
        }

        return this;
    }

    getEditable() {
        return this.isEditable;
    }

    getShape(shapeId) {
        return this.shapes[shapeId] ? this.shapes[shapeId].shape : null;
    }

    getShapeDetail(shapeId) {
        if (!this.shapes[shapeId]) return null;
        const shapeItem = this.shapes[shapeId];
        const { shape, shapeGroupId } = shapeItem;
        const { cropName, typeId, customerName } = this.shapeGroups[shapeGroupId];
        return {
            ...shapeItem,
            ...this.getShapeInstance().getAttribution(shape),
            id: shapeId,
            cropName,
            typeId,
            customerName,
            properties: {
                fillColor: shape.get("fillColor")
            },
            editable: (shapeGroupId === this.editableShapeGroupId)
        };
    }

    getShapeInstance() {
        if (!this.shapeObj) this.shapeObj = new Shape(this.google);
        return this.shapeObj;
    }

    addListener(...agrs) {
        super.addListener(...agrs);
        return this;
    }

    usePlugin(name, instance) {
        if (!this.plugins.hasOwnProperty(name)) {
            this.plugins[name] = instance;
            instance.init(this);
        }
    }
}

export class Shape {
    constructor(google) {
        this.google = google;
    }

    createShape(options) {
        const t = this.google.maps.drawing.OverlayType;
        const methods = {
            [t.MARKER]: this.createMarker,
            [t.POLYLINE]: this.createPolyline,
            [t.POLYGON]: this.createPolygon,
            [t.RECTANGLE]: this.createRectange,
            [t.CIRCLE]: this.createCircle
        };

        if (methods.hasOwnProperty(options.type)) {
            methods[options.type] = methods[options.type].bind(this);
            return methods[options.type](options);
        }
        return;
    }

    createMarker(options) {
        return new this.google.maps.Marker({
            ...options.properties,
            id: options.id,
            draggable: false,
            position: options.location.length > 0 ? options.location[0] : null
        });
    }

    createPolyline(options) {
        return new this.google.maps.Polyline({
            ...options.properties,
            id: options.id,
            geodesic: true,
            draggable: false,
            path: options.location
        });
    }

    createPolygon(options) {
        return new this.google.maps.Polygon({
            ...options.properties,
            id: options.id,
            draggable: false,
            path: options.location
        });
    }

    createRectange(options) {
        const shape = new this.google.maps.Rectangle({
            ...options.properties,
            id: options.id,
            draggable: false
        });

        if (options.location.length >= 2) {
            const loc = options.location;
            const bounds = new this.google.maps.LatLngBounds(
                new this.google.maps.LatLng(loc[loc.length - 1].lat, loc[loc.length - 1].lng),
                new this.google.maps.LatLng(loc[0].lat, loc[0].lng)
            );
            shape.setBounds(bounds);
        }
        return shape;
    }

    createCircle(options) {
        const shape = new this.google.maps.Circle({
            ...options.properties,
            id: options.id,
            draggable: false
        });

        if (options.location.length > 0) {
            const loc = options.location;
            const center = new this.google.maps.LatLng(loc[0].lat, loc[0].lng);
            shape.setCenter(center);
        }

        return shape;
    }

    editShape(shape, options) {
        const t = this.google.maps.drawing.OverlayType;
        const methods = {
            [t.MARKER]: this.editMarker,
            [t.POLYLINE]: this.editPolyline,
            [t.POLYGON]: this.createPolygon,
            [t.RECTANGLE]: this.createRectange,
            [t.CIRCLE]: this.createCircle
        };

        if (methods.hasOwnProperty(shape.type)) {
            methods[shape.type] = methods[shape.type].bind(this);
            return methods[shape.type](options);
        }
        return;
    }

    editMarker(shape, options) {
        if (!shape) return;
        if (options.properties) shape.setOptions(options.properties);
        if (options.position) shape.setPosition(options.position);
    }

    editPolyline(shape, options) {
        if (!shape) return;
        if (options.properties) shape.setOptions(options.properties);
        if (options.path) shape.setPath(options.path);
    }

    getAttribution(shape) {
        let area = null, distance = null;

        const shapeType = shape ? shape.type : null;
        const type = this.google.maps.drawing.OverlayType;

        if (shapeType === type.POLYGON) {
            area = (this.google.maps.geometry.spherical.computeArea(shape.getPath()) * 0.00024711).toFixed(2); //square meter to acre
        } else if (shapeType === type.CIRCLE) {
            area = (3.14 * shape.radius * shape.radius * 0.00024711).toFixed(2); //square meter to acre
        } else if (shapeType === type.RECTANGLE) {
            const bounds = shape.getBounds();
            const ne = bounds.getNorthEast();
            const sw = bounds.getSouthWest();
            const se = new this.google.maps.LatLng(sw.lat(), ne.lng());
            const breadth = this.getDistance([ne, se]);
            const length = this.getDistance([se, sw]);
            area = (length * breadth * 0.00024711).toFixed(2); //square meter to acre
        } else if (shapeType === type.POLYLINE) {
            const pathList = shape.getPath().getArray();
            distance = this.getDistance([pathList[0], pathList[pathList.length - 1]]);
            distance = (Math.sqrt(distance)).toFixed(2); //meter
        }

        return { area, distance };
    }

    getLocation(shape, latLngObject = false) {
        const shapeType = shape ? shape.type : null;
        const type = this.google.maps.drawing.OverlayType;
        let location = [];

        if (shapeType === type.POLYGON || shapeType === type.POLYLINE) {
            location = latLngObject ? shape.getPath().getArray() :
                shape.getPath().getArray().map(e => ({ lat: e.lat(), lng: e.lng() }));
        } else if (shapeType === type.CIRCLE) {
            const center = shape.getCenter();
            location = latLngObject ? [center] : [{ lat: center.lat(), lng: center.lng() }];
        } else if (shapeType === type.RECTANGLE) {
            const bounds = shape.getBounds();
            const ne = bounds.getNorthEast();
            const sw = bounds.getSouthWest();
            location = latLngObject ? [ne, sw] : [{ lat: ne.lat(), lng: ne.lng() }, { lat: sw.lat(), lng: sw.lng() }];
        }
        return location;
    }

    getShapeOptions(shape, isLocation) {
        const props = { properties: {}, location: [] };

        if (shape.type === this.google.maps.drawing.OverlayType.MARKER) {
            props.properties = { icon: null, others: shape.get('others') };

            if (isLocation) props.location.push({ lat: shape.position.lat(), lng: shape.position.lng() });
            else props.position = shape.position;
            return props;
        } else if (shape.type === this.google.maps.drawing.OverlayType.POLYLINE) {
            props.properties = {
                strokeColor: shape.get('strokeColor'),
                strokeOpacity: shape.get('strokeOpacity'),
                strokeWeight: shape.get('strokeWeight'),
                others: shape.get('others')
            };

            if (isLocation) props.location = shape.getPath().getArray().map(e => ({ lat: e.lat(), lng: e.lng() }));
            else props.path = shape.getPath();
            return props;
        } else if (shape.type === this.google.maps.drawing.OverlayType.POLYGON) {
            props.properties = {
                strokeColor: shape.get('strokeColor'),
                strokeOpacity: shape.get('strokeOpacity'),
                strokeWeight: shape.get('strokeWeight'),
                fillColor: shape.get('fillColor'),
                fillOpacity: shape.get('fillOpacity'),
                others: shape.get('others')
            };

            if (isLocation) props.location = shape.getPath().getArray().map(e => ({ lat: e.lat(), lng: e.lng() }));
            else props.path = shape.getPath();
            return props;
        } else if (shape.type === this.google.maps.drawing.OverlayType.RECTANGLE) {
            props.properties = {
                strokeColor: shape.get('strokeColor'),
                strokeOpacity: shape.get('strokeOpacity'),
                strokeWeight: shape.get('strokeWeight'),
                fillColor: shape.get('fillColor'),
                fillOpacity: shape.get('fillOpacity'),
                others: shape.get('others')
            };

            if (isLocation) {
                const bounds = shape.getBounds();
                const northEast = bounds.getNorthEast();
                const southWest = bounds.getSouthWest();
                props.location.push({ lat: northEast.lat(), lng: northEast.lng() });
                props.location.push({ lat: southWest.lat(), lng: southWest.lng() });
            }
            else props.bounds = shape.getBounds();
            return props;
        } else if (shape.type === this.google.maps.drawing.OverlayType.CIRCLE) {
            props.properties = {
                strokeColor: shape.get('strokeColor'),
                strokeOpacity: shape.get('strokeOpacity'),
                strokeWeight: shape.get('strokeWeight'),
                fillColor: shape.get('fillColor'),
                fillOpacity: shape.get('fillOpacity'),
                radius: shape.getRadius(),
                others: shape.get('others')
            };

            if (isLocation) {
                const center = shape.getCenter();
                props.location.push({ lat: center.lat(), lng: center.lng() });
            } else props.center = shape.getCenter();
            return props;
        }

        return props;
    }

    addClickListener(shape, listener) {
        const google = this.google;

        if (shape.type === google.maps.drawing.OverlayType.MARKER) {
            google.maps.event.addListener(shape, 'click', e => {
                listener(e.latLng, e);
            });
        }
        else {
            // Add an event listener that selects the newly-drawn shape when the user
            // mouses down on it.
            google.maps.event.addListener(shape, 'click', e => {
                if (!e) return;
                if (e.vertex !== undefined) {
                    if (shape.type === google.maps.drawing.OverlayType.POLYGON) {
                        const path = shape.getPaths().getAt(e.path);
                        path.removeAt(e.vertex);
                        if (path.length < 3) {
                            shape.setMap(null);
                        }
                    }
                    else if (shape.type === google.maps.drawing.OverlayType.POLYLINE) {
                        const path = shape.getPath();
                        path.removeAt(e.vertex);
                        if (path.length < 2) {
                            shape.setMap(null);
                        }
                    }
                }

                listener(e.latLng, e);
            });
        }

        return this;
    }

    getDistance(coordinates) {
        const R = 6371 * 1000; //km to meter
        const lat1 = coordinates[0].lat();
        const lng1 = coordinates[0].lng();
        const lat2 = coordinates[1].lat();
        const lng2 = coordinates[1].lng();
        const deg2rad = (deg) => deg * (Math.PI / 180);
        const dLat = deg2rad(lat2 - lat1);
        const dLon = deg2rad(lng2 - lng1);
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const d = R * c;
        return d;
    }
}